import { Component, OnInit, Signal, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { ActivatedRoute } from '@angular/router';
import { AssetStateMessageHandlingService } from '@r1-compass-library/services/asset-state-message-handling.service';
import { LifecycleRulesetStore } from '../store/lifecycle-ruleset.store';
import { filterNil } from 'ngxtension/filter-nil';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { LifecycleRulesDefinitionForm } from '@r1-compass-library/models/lifecycle-rules/lifecycle-rules-form.model';
import { InstructionsComponent } from '@r1-compass-library/components';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { LifecycleRuleset } from '@r1-compass-library/models/lifecycle-rules/lifecycle-ruleset.model';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import {
    FormUnsavedChangesWarningEntity,
    noopFormSaveMutationResult,
} from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import { SaveGuardStore } from '@r1-compass-library/guards/save-guard.store';
import { injectParams } from 'ngxtension/inject-params';
import { LifecycleRulesValidationService, LifecycleRulesSectionFormMap } from '../lifecycle-rules-validation.service';
import { LifecycleRulesValidationStateService } from '../lifecycle-rules-validation-state.service';
import {
    SyncValidationStateSection,
    SyncValidationStateService,
    ValidationService,
    ValidationStateService,
    LifecycleRulesSectionName,
} from '@r1-compass-library/features/validation-sync';
import { AssetInvalidSaveWarningService } from '../../asset-invalid-save-warning.service';

@Component({
    selector: 'r1-compass-app-lifecycle-rules-definition',
    templateUrl: './lifecycle-rules-definition.component.html',
    styleUrl: './lifecycle-rules-definition.component.scss',
    imports: [InstructionsComponent, MatButtonModule, MatFormFieldModule, MatInputModule, ReactiveFormsModule],
})
export class LifecycleRulesDefinitionComponent
    implements
        OnInit,
        FormUnsavedChangesWarningEntity,
        SyncValidationStateSection<LifecycleRulesSectionName, LifecycleRuleset>
{
    readonly #route = inject(ActivatedRoute);
    readonly #rulesetStore = inject(LifecycleRulesetStore);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #assetStateMessageHandlingService = inject(AssetStateMessageHandlingService);
    readonly #formSaveWarningStore = inject(SaveGuardStore);
    readonly #validationService = inject(LifecycleRulesValidationService);
    readonly #validationStateService = inject(LifecycleRulesValidationStateService);
    readonly #syncValidationStateService = inject(SyncValidationStateService);
    readonly #invalidSaveWarningService = inject(AssetInvalidSaveWarningService);

    definitionForm = new FormGroup<LifecycleRulesDefinitionForm>(new LifecycleRulesDefinitionForm());
    loggedInUserId: Signal<number | undefined> = this.#globalLoggedInUserStore.selectedUserId;

    readonly canDeactivate$ = this.#formSaveWarningStore.deriveCanDeactivate$(this.definitionForm);
    readonly saveGuardHandlerFn = this.#formSaveWarningStore.formUnsavedChangesWarningEntityDefinition;

    readonly allowPartialSave = true;

    private readonly canDeactivate = toSignal(this.canDeactivate$, { requireSync: true });

    public disableSave = computed<boolean>(() =>
        this.#validationStateService.shouldDisableSave(
            this.#rulesetStore.lifecycleRuleset(),
            this.canDeactivate(),
            this.#validationStateService.invalidSections(),
            'definition'
        )
    );

    constructor() {
        this.saveGuardHandlerFn({
            form: this.definitionForm,
            canDeactivate$: this.canDeactivate$,
            params: injectParams('id'),
            entity$: this.#rulesetStore.lifecycleRuleset$,
            savedSoCanRoute$: this.#formSaveWarningStore.savedSoCanRoute$,
            route: this.#route,
            disableSave: this.disableSave,
        });
    }

    ngOnInit(): void {
        const rulesetId: string = this.#route.snapshot.params['id'];
        if (rulesetId && rulesetId !== 'new') {
            this.#rulesetStore.lifecycleRuleset$.pipe(filterNil()).subscribe(ruleset => {
                this.definitionForm.setValue(
                    new FormGroup<LifecycleRulesDefinitionForm>(new LifecycleRulesDefinitionForm(ruleset)).getRawValue()
                );
                this.subscribeToValidationState(
                    this.definitionForm,
                    'definition',
                    this.#validationService,
                    this.#validationStateService
                );
                this.definitionForm.markAllAsTouched();
            });
            if (this.#rulesetStore.lifecycleRuleset()?.id !== +rulesetId) {
                this.#rulesetStore.getLifecycleRuleset(+rulesetId);
            }
        } else {
            this.definitionForm.setValue(
                new FormGroup<LifecycleRulesDefinitionForm>(new LifecycleRulesDefinitionForm()).getRawValue()
            );
        }

        this.subscribeToValidationState(
            this.definitionForm,
            'definition',
            this.#validationService,
            this.#validationStateService
        );
        this.definitionForm.markAllAsTouched(); // this makes the form indicate invalid fields on load

        this.#rulesetStore.message$.pipe(filterNil()).subscribe(message => {
            this.#assetStateMessageHandlingService.handleMessage(message);
            this.#rulesetStore.resetMessage();
        });
    }

    async onSave() {
        const divisionId = this.#globalLoggedInUserStore.selectedIamDivision()?.id;
        if (divisionId) {
            let rulesetToSave: Partial<LifecycleRuleset> = {
                ...this.definitionForm.getRawValue(),
                updatedBy: this.loggedInUserId(),
                valid: this.#validationStateService.invalidSections().length === 0,
            };
            const rulesetId: string = this.#route.snapshot.params['id'];
            if (rulesetId === 'new') {
                rulesetToSave = {
                    ...rulesetToSave,
                    ...this.createNewLifecycleRuleset(divisionId),
                };
            } else if (rulesetId) {
                rulesetToSave.id = +rulesetId;
            }
            if (!(await this.#invalidSaveWarningService.confirmSave('lifecycle rule', rulesetToSave))) {
                return noopFormSaveMutationResult();
            }
            return this.#rulesetStore.saveLifecycleRuleset(rulesetToSave);
        } else {
            return noopFormSaveMutationResult();
        }
    }

    subscribeToValidationState(
        form: FormGroup<LifecycleRulesDefinitionForm>,
        sectionName: LifecycleRulesSectionName,
        validationService: ValidationService<LifecycleRulesSectionName, LifecycleRuleset, LifecycleRulesSectionFormMap>,
        validationStateService: ValidationStateService<LifecycleRulesSectionName>
    ) {
        return this.#syncValidationStateService.subToValidationState(
            form,
            sectionName,
            validationService,
            validationStateService
        );
    }

    private createNewLifecycleRuleset(divisionId: number): Partial<LifecycleRuleset> {
        return {
            iamDivisionId: divisionId,
            lifecycleRules: [],
            folderId: this.checkForRootFolder(),
            updatedBy: this.loggedInUserId(),
            createdBy: this.loggedInUserId(),
        };
    }

    /**
     * Method for determining if asset is in Root folder or not.
     * Returns "undefined" if folderId is 0, as 0 is assumed to be Root.
     * Non-folder assets cannot have folderIds if they are created in Root, or else they will error due to database config.
     * @param id The current folderId
     * @private
     */
    private checkForRootFolder() {
        const id = Number(this.#route.snapshot.queryParams['folderId']);
        if (id === 0) {
            return undefined;
        } else {
            return id;
        }
    }
}
